import React, { useCallback, useState, type KeyboardEvent, type SyntheticEvent } from 'react';
import { Box, Flex, xcss } from '@atlaskit/primitives';
import type { DocNode } from '@atlaskit/adf-schema';
import { defaultSchema } from '@atlaskit/adf-schema/schema-default';
import { ReactRenderer } from '@atlaskit/renderer';
import { useIntl } from '@atlassian/jira-intl';
import { useEmojiProvider } from '@atlassian/jira-emoji-provider/src/ui/index.tsx';
import { convertWikiToAdf } from '@atlassian/jira-platform-convert-wiki-to-adf/src/main.tsx';
import {
	onLinkClick,
	smartLinksDefault,
} from '@atlassian/jira-linking-platform-utils/src/index.tsx';
import { RelativeTime } from '../../../../../common/ui/relative-time/index.tsx';
import { UserAvatar } from '../../../../../common/ui/user-avatar/index.tsx';
import {
	ActivityTypes,
	BoundaryIds,
	SectionIds,
	type Activity,
} from '../../../../../common/types.tsx';
import { ActivityMessage } from '../../../../../common/ui/activity-message/index.tsx';

import { useSectionAnalytics } from '../../../../../common/ui/analytics/index.tsx';
import messages from './messages.tsx';

export type CommentAddedProps = {
	activity: Activity<'COMMENT_ADDED'>;
};

export const CommentAdded = ({
	activity: {
		created,
		issueData,
		author,
		data: { comment },
	},
}: CommentAddedProps) => {
	const [isCommentExpanded, setIsCommentExpanded] = useState(false);
	const emojiProvider = useEmojiProvider();
	const { formatMessage } = useIntl();
	const { fireSectionAnalytics } = useSectionAnalytics(
		SectionIds.RecentActivity,
		BoundaryIds.RecentActivity,
	);

	const adf = convertWikiToAdf(comment);

	const onActivityItemClick = useCallback(
		(target: 'user' | 'issue') => {
			fireSectionAnalytics(target, {
				boundaryId: BoundaryIds.RecentActivity,
				activityType: ActivityTypes.COMMENT_ADDED,
			});
		},
		[fireSectionAnalytics],
	);

	const onCommentLinkClick = useCallback(
		(event: SyntheticEvent<HTMLAnchorElement>, url?: string) => {
			onLinkClick(event, url);
			fireSectionAnalytics('link', {
				boundaryId: BoundaryIds.RecentActivity,
				activityType: ActivityTypes.COMMENT_ADDED,
			});
		},
		[fireSectionAnalytics],
	);

	return (
		<Box>
			<Flex columnGap="space.100">
				<UserAvatar author={author} />
				<Flex direction="column" rowGap="space.050">
					<ActivityMessage author={author} issueData={issueData} onClick={onActivityItemClick}>
						{({ author: activityAuthor, issueLink }) =>
							formatMessage(messages.commentAddedContent, {
								author: activityAuthor,
								issueLink,
							})
						}
					</ActivityMessage>
					<RelativeTime time={created} />
					<Box
						role="button"
						aria-expanded={isCommentExpanded}
						tabIndex={0}
						onClick={() => {
							setIsCommentExpanded((prev) => !prev);
						}}
						onKeyDown={(event: KeyboardEvent) => {
							if (event.key === 'Enter' || event.key === ' ') {
								event.preventDefault();
								setIsCommentExpanded((prev) => !prev);
							}
						}}
						xcss={commentWrapperStyles}
					>
						<ReactRenderer
							// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
							document={adf as DocNode}
							schema={defaultSchema}
							truncated={!isCommentExpanded}
							dataProviders={emojiProvider}
							eventHandlers={{
								smartCard: { onClick: onCommentLinkClick },
								link: { onClick: onCommentLinkClick },
							}}
							smartLinks={smartLinksDefault}
						/>
					</Box>
				</Flex>
			</Flex>
		</Box>
	);
};

const commentWrapperStyles = xcss({
	marginTop: 'space.100',
	padding: 'space.100',
	border: '1px solid',
	borderColor: 'color.border',
	borderRadius: 'border.radius',
	cursor: 'pointer',
	':hover': {
		backgroundColor: 'color.background.neutral.subtle.hovered',
	},
	':focus': {
		outline: '1px solid',
		outlineColor: 'color.border.focused',
	},
});
